import { useAtom } from 'jotai';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { ChangeEvent } from 'react';
import { authAtom } from '../atoms';
import { ChangePassword } from '../components/ChangePassword';
import { USER_ROLE, TOAST_DURATION } from '../lib/constants';
import {
  useProjectsQuery,
  useRefreshTokenMutation,
  useUpdateLanguageMutation,
} from '../lib/queries';
import LanguageSelector from '../components/LanguageSelector';

export default function SettingsPage() {
  const { t, i18n } = useTranslation();
  const [auth, setAuth] = useAtom(authAtom);
  const projectsQuery = useProjectsQuery();
  const projects = projectsQuery.data ? projectsQuery.data?.projects : [];

  const updateLanguageMutation = useUpdateLanguageMutation();
  const refreshTokenMutation = useRefreshTokenMutation();

  if (!auth?.user) return null;
  const { user } = auth;

  const handleLanguageChange = (event: ChangeEvent<HTMLSelectElement>) => {
    const language = event.target.value;
    updateLanguageMutation.mutate(language, {
      onSuccess: async () => {
        setAuth((prevAuth) => {
          if (prevAuth?.user) {
            return {
              ...prevAuth,
              user: { ...prevAuth.user, language },
            };
          }

          return prevAuth;
        });
        i18n.changeLanguage(language);
        if (auth?.refreshToken) {
          try {
            await refreshTokenMutation.mutateAsync({ refreshToken: auth.refreshToken });
            toast.success(t('settings.languageChange'), {
              duration: TOAST_DURATION,
            });
          } catch (err) {
            toast.error(t('settings.languageChangeError'), {
              duration: TOAST_DURATION,
            });
          }
        }
      },
      onError: () => {
        toast.error(t('settings.languageChangeError'), {
          duration: TOAST_DURATION,
        });
      },
    });
  };

  return (
    <main className="grid gap-4 w-700 mx-auto">
      <h1 className="text-hero font-bold col-span-2">{t('settings.title')}</h1>

      <h3 className="col-span-2 font-bold text-title pt-10">{t('settings.user')}</h3>
      <span className="font-semibold">{t('settings.userName')}</span>
      <span>{user.name}</span>

      <span className="font-semibold">{t('common.email')}</span>
      <span>{user.email}</span>

      {user.role === USER_ROLE.SUPERADMIN && (
        <>
          <span className="font-semibold">{t('settings.userRole')}</span>
          <span>{t(`user.${user.role}`)}</span>
        </>
      )}

      <span className="font-semibold">{t('settings.createdAt')}</span>
      <span>{new Date(user.createdAt).toLocaleDateString()}</span>

      <span className="font-semibold">{t('settings.projects')}</span>
      <summary className="grid grid-cols-[auto_max-content] grid-rows-1')">
        {projects.length && <span>{projects.map((project) => project.name).join(', ')}</span>}
      </summary>

      <span className="font-semibold flex items-center">{t('common.language')}</span>
      <LanguageSelector onChange={handleLanguageChange} value={user.language} />

      <h3 className="col-span-2 font-bold text-title pt-10">{t('settings.passwordChange')}</h3>
      <ChangePassword />
    </main>
  );
}
